import React from 'react'
import { useSelector, shallowEqual } from 'react-redux'

import {
  useRelatedContentForTargets,
  useServedEntityKinds,
} from '../hooks/RelatedContent'
import { RelatedContentButton as ContentModalButton } from './RelatedContent'

/**
 * Related Content entry point for one entity.
 *
 * Wraps the modal with the fetch and the two gates every surface needs, so a
 * caller only supplies what identifies its entity. Renders nothing at all when
 * the provider has no content for that entity kind -- a Wiretronics customer
 * has connector tooling and nothing else, and a Concept-T customer has whatever
 * the templates table has switched on.
 */
const RelatedContentFor = ({
  targets,
  entityKind,
  details,
  vin: vinProp,
  onBeforeOpen,
  pin,
  buttonTheme,
  ...identifiers
}) => {
  // Most surfaces sit inside an open vehicle test. Global Search does not -- it
  // carries its own VIN -- so one may be passed explicitly.
  const openTestVin = useSelector(
    (state) => state.vehicleTests.currentVehicleTest?.vin,
    shallowEqual,
  )
  const vin = vinProp || openTestVin

  // A single kind is the common case; a connector needs several, since its
  // documentation and its tooling are published under different kinds.
  const entityTargets = targets ?? [{ entityKind, ...identifiers }]

  const { isEnabled, served } = useServedEntityKinds()
  const servedTargets = entityTargets.filter((target) =>
    served.includes(target.entityKind),
  )

  const { contents, isLoading } = useRelatedContentForTargets({
    targets: servedTargets,
    vin,
    enabled: isEnabled && !!vin,
  })

  // Entities with no pin dimension come back under "all"; a connector part also
  // merges that bucket, since maintenance tools are published per connector.
  const steps = contents.flatMap((content) => [
    ...(content?.[pin] || []),
    ...(content?.all || []),
  ])

  // A provider that serves none of these kinds has nothing to say, so no button.
  // Having nothing for this particular entity is different: the button stays and
  // goes disabled, matching Related Diagrams beside it.
  if (!isEnabled || servedTargets.length === 0) {
    return null
  }

  return (
    <ContentModalButton
      tools={steps}
      pin={details}
      isLoading={isLoading}
      onBeforeOpen={onBeforeOpen}
      buttonTheme={buttonTheme}
    />
  )
}

export default RelatedContentFor
